import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, inject } from '@angular/core';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import { assertDefined } from '@siza-staff-care/shared/utils';
import { Observable, Subject, exhaustMap, interval, switchMap, takeUntil, takeWhile, tap, withLatestFrom } from 'rxjs';
import { AuthStore } from './auth.store';
import { SessionTimeoutDialogService } from './components/session-timeout-dialog-component';
import { JadeService } from './jade.service';

const STORE_KEY = 'userLastAction';
const DEBUG = false;
const DEBUG_KEEP_ALIVE = false;

type State = {
	warningSeconds?: number;
	timeoutCountdownSeconds?: number;
	listenerInitialised?: boolean;

	inWarning?: boolean;
	forceEnd?: boolean;
	remainingSeconds?: number;
	timeoutSeconds?: number;
	countdownStartSeconds?: number;
};

@Injectable({
	providedIn: 'root',
})
export class SessionTimeoutService extends ComponentStore<State> {
	public static runIdleTimer: boolean;
	public static runCountdownTimer: boolean;

	private intervalEnd$ = new Subject<void>();

	readonly warningSeconds$ = this.select(({ warningSeconds }) => warningSeconds ?? 0);
	readonly timeoutCountdownSeconds$ = this.select(({ timeoutCountdownSeconds }) => timeoutCountdownSeconds ?? 0);

	readonly listenerInitialised$ = this.select(({ listenerInitialised }) => !!listenerInitialised);

	readonly inWarning$ = this.select(({ inWarning }) => !!inWarning);
	readonly remainingSeconds$ = this.select(({ remainingSeconds }) => remainingSeconds ?? 0);
	readonly countdownStartSeconds$ = this.select(({ countdownStartSeconds }) => countdownStartSeconds ?? 0);
	readonly timeoutSeconds$ = this.select(({ timeoutSeconds }) => timeoutSeconds ?? 0);
	readonly forceEnd$ = this.select(({ forceEnd }) => !!forceEnd);

	readonly vm$ = this.select({
		inWarning: this.inWarning$,
		remainingSeconds: this.remainingSeconds$,
		timeoutSeconds: this.timeoutSeconds$,
		forceEnd: this.forceEnd$,
	});

	private sessionEnded = new Subject<void>();
	public readonly sessionEnded$ = this.sessionEnded.asObservable();

	private userActivityChangeCallback?: ($event) => void;

	private zone = inject(NgZone);
	private appState = inject(AppStateService);
	private jade = inject(JadeService);
	private dialog = inject(SessionTimeoutDialogService);

	private authStore?: AuthStore;

	private clickListener?: void;
	private mouseListener?: void;

	constructor(@Inject(DOCUMENT) private document: Document) {
		super({});

		this.dialog.watchForSessionTimeoutWarning(this.inWarning$);
	}

	get lastAction(): number {
		return Number(sessionStorage.getItem(STORE_KEY));
	}

	set lastAction(value) {
		sessionStorage.setItem(STORE_KEY, value.toString());
	}

	private sessionForIdle$ = interval(1000).pipe(takeWhile(() => SessionTimeoutService.runIdleTimer));
	private clockForIdle$ = interval(1000).pipe(takeWhile(() => SessionTimeoutService.runCountdownTimer));

	private readonly sendKeepAlive = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				// Send a fire-and-forget request to the server to keep the session alive.
				return this.jade.postKeepAlive().pipe(
					tapResponse(
						() => {
							// Nothing to do here.
						},
						(err) => {
							console.warn('Error sending keep-alive request', err);
						},
					),
				);
			}),
		),
	);

	readonly setAuthStore = this.effect((authStore$: Observable<AuthStore>) =>
		authStore$.pipe(
			tap((authStore) => {
				assertDefined(authStore);
				this.authStore = authStore;
			}),
		),
	);

	readonly startSession = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() => {
				const keepAliveMinutes = this.appState.settings.apiSessionKeepAliveMinutes || 2;
				let keepAliveMS = keepAliveMinutes * 60 * 1000;

				if (DEBUG_KEEP_ALIVE) {
					keepAliveMS = 15000;
				}

				this.setUpSessionTimeout();

				return interval(keepAliveMS).pipe(
					tap(() => this.sendKeepAlive()),
					takeUntil(this.intervalEnd$),
				);
			}),
		),
	);

	readonly endSession = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				DEBUG && console.log('endSession');
				this.intervalEnd$.next();

				this.sessionEnded.next();
				this.stop();
			}),
		),
	);

	private readonly setUpSessionTimeout = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					inWarning: false,
				});

				this.loadTimeoutsFromSettings();
				this.resume();
			}),
		),
	);

	private stop() {
		SessionTimeoutService.runIdleTimer = false;
		SessionTimeoutService.runCountdownTimer = false;
	}

	public resume() {
		SessionTimeoutService.runCountdownTimer = false;
		this.initializeSessionTimeout();
	}

	private initializeSessionTimeout() {
		this.reset();
		//SCS-101: Change timeout parameters for Staff.Care
		SessionTimeoutService.runIdleTimer = true;
		this.initListener();
		this.initInterval();

		SessionTimeoutService.runIdleTimer = true;
	}

	private readonly initListener = this.effect((trigger$) =>
		trigger$.pipe(
			takeWhile(() => SessionTimeoutService.runIdleTimer),
			withLatestFrom(this.listenerInitialised$),
			tap(([, listenerInitialised]) => {
				if (listenerInitialised) {
					return;
				}

				this.zone.runOutsideAngular(() => {
					this.userActivityChangeCallback = ($event) => this.handleUserActiveState($event);

					this.clickListener = this.document.addEventListener(
						'click',
						this.userActivityChangeCallback.bind(this),
						true,
					);
					this.mouseListener = this.document.addEventListener(
						'keydown',
						this.userActivityChangeCallback.bind(this),
						true,
					);
				});

				this.patchState({
					listenerInitialised: true,
				});
			}),
		),
	);

	private readonly initInterval = this.effect((trigger$) =>
		trigger$.pipe(
			exhaustMap(() => {
				return this.sessionForIdle$.pipe(
					withLatestFrom(this.warningSeconds$, this.timeoutCountdownSeconds$),
					tap(([, warningSeconds, timeoutCountdownSeconds]) => {
						const now = Date.now();
						const secondsLeft = this.lastAction + warningSeconds * 1000;
						const diffSeconds = (secondsLeft - now) / 1000;
						const isTimeout = diffSeconds < 0;

						if (isTimeout && Math.abs(diffSeconds) > timeoutCountdownSeconds) {
							// User is already beyond the warning and countdown. Don't show the warning but simply end the session.
							SessionTimeoutService.runIdleTimer = false;
							this.patchState({
								forceEnd: true,
							});

							this.endSession();
							this.authStore?.logOut({ logoutFromTimeout: true, message: 'Session timeout' });

							return;
						} else if (isTimeout) {
							const remainingWarning = timeoutCountdownSeconds - Math.abs(diffSeconds);
							const countdownRemaining = Math.floor(remainingWarning);

							this.patchState({
								countdownStartSeconds: countdownRemaining,
							});
						}

						DEBUG &&
							console.log(
								`warning seconds ${warningSeconds}, timeoutCountdownSeconds ${timeoutCountdownSeconds}, until warning ${Math.floor(
									diffSeconds,
								)} secs = ${Math.floor(diffSeconds / 60)} minutes`,
							);

						if (isTimeout) {
							this.clickListener && this.document.removeEventListener('click', this.clickListener, true);
							this.mouseListener &&
								this.document.removeEventListener('keydown', this.mouseListener, true);
							this.zone.run(() => {
								if (timeoutCountdownSeconds > 0) {
									this.executeFinalTimer();
								}
								SessionTimeoutService.runIdleTimer = false;
							});
						}
					}),
				);
			}),
		),
	);

	private readonly executeFinalTimer = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				SessionTimeoutService.runCountdownTimer = true;
				this.initializeFinalTimer();
			}),
		),
	);

	private readonly initializeFinalTimer = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.countdownStartSeconds$),
			exhaustMap(([, countdownStartSeconds]) => {
				let remainingSeconds = countdownStartSeconds;

				let now = Date.now();
				const logoutTime = now + remainingSeconds * 1000;

				DEBUG && console.log('checkUserActionTime - remainingSeconds = ' + remainingSeconds);
				DEBUG && console.log('logoutTime calculated as ' + new Date(logoutTime));

				this.patchState({
					inWarning: true,
					remainingSeconds,
					timeoutSeconds: remainingSeconds,
				});

				return this.clockForIdle$.pipe(
					tap(() => {
						now = Date.now();
						remainingSeconds = Math.floor((logoutTime - now) / 1000);

						DEBUG && console.log('checkUserActionTime - remainingSeconds = ' + remainingSeconds);

						if (remainingSeconds <= 0) {
							SessionTimeoutService.runCountdownTimer = false;
							this.patchState({
								forceEnd: true,
							});
						}

						this.patchState({
							remainingSeconds,
						});
					}),
				);
			}),
		),
	);

	private loadTimeoutsFromSettings() {
		const settings = this.appState.settings;

		const timeoutSeconds = DEBUG ? 30 : (settings.timeoutMinutes || 60) * 60;
		const warningSeconds = DEBUG ? 10 : (settings.timeoutWarningMinutes || 5) * 60;

		const timeoutCountdownSeconds = timeoutSeconds - warningSeconds;

		if (DEBUG) {
			console.log(`timeoutSeconds: ${timeoutSeconds} = ${Math.floor(timeoutSeconds / 60)} minutes`);
			console.log(
				`timeoutCountdownSeconds: ${timeoutCountdownSeconds} = ${Math.floor(
					timeoutCountdownSeconds / 60,
				)} minutes`,
			);
		}

		this.patchState({
			warningSeconds,
			timeoutCountdownSeconds,
		});
	}

	private readonly handleUserActiveState = this.effect((event$: Observable<unknown>) =>
		event$.pipe(
			tap(() => {
				DEBUG && console.log('saw some user activity - resetting countdown');
				this.reset();
			}),
		),
	);

	private readonly reset = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchState({
					inWarning: false,
					forceEnd: false,
				});
				this.lastAction = Date.now();
			}),
		),
	);
}
